// Display formatting. Dates arrive as calendar strings ('2026-08-29') already in the
// user's timezone, so they are formatted as plain dates — never passed through
// `new Date(...)` and re-localised, which would shift them a day in either direction.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function parts(date: string): { y: number; m: number; d: number } {
  return { y: Number(date.slice(0, 4)), m: Number(date.slice(5, 7)), d: Number(date.slice(8, 10)) };
}

/** "29 Aug" */
export function shortDate(date: string): string {
  const { m, d } = parts(date);
  return `${d} ${MONTHS[m - 1] ?? ''}`;
}

/** "29 Aug 2026" */
export function longDate(date: string): string {
  const { y, m, d } = parts(date);
  return `${d} ${MONTHS[m - 1] ?? ''} ${y}`;
}

/** "Saturday 29 August" */
export function weekdayDate(date: string): string {
  const { y, m, d } = parts(date);
  const weekday = DAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()] ?? '';
  return `${weekday} ${d} ${MONTHS[m - 1] ?? ''}`;
}

/** "today", "yesterday", "in 3 days", "12 days ago" */
export function relativeDate(date: string, today: string): string {
  const toMs = (s: string) => { const p = parts(s); return Date.UTC(p.y, p.m - 1, p.d); };
  const days = Math.round((toMs(date) - toMs(today)) / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  return days > 0 ? `in ${days} days` : `${-days} days ago`;
}

export function titleCase(value: string): string {
  return value.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

/** Today, in the browser's own timezone — used only for form defaults. */
export function todayLocal(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

export function addDaysTo(date: string, days: number): string {
  const p = parts(date);
  return new Date(Date.UTC(p.y, p.m - 1, p.d) + days * 86_400_000).toISOString().slice(0, 10);
}

export const CONFOUNDER_LABELS: Record<string, string> = {
  illness: 'Illness',
  travel: 'Travel',
  alcohol: 'Alcohol',
  unusual_exercise: 'Unusual exercise',
  poor_sleep: 'Poor sleep',
  stress: 'Stress',
  late_meal: 'Late meal',
  missed_medication: 'Missed medication',
  new_medication: 'New medication',
  weather: 'Weather',
  unusual_food: 'Unusual food',
  other: 'Something else',
};

/** Red-flag chips offered at check-in. The wording is deliberately plain. */
export const RED_FLAG_CHIPS: Array<{ id: string; label: string }> = [
  { id: 'chest_pain', label: 'Chest pain' },
  { id: 'severe_breathlessness', label: 'Severe breathlessness' },
  { id: 'fainting', label: 'Fainting' },
  { id: 'severe_allergic_reaction', label: 'Severe allergic reaction' },
  { id: 'significant_blood_loss', label: 'Unexplained bleeding' },
  { id: 'severe_neurological', label: 'Weakness / slurred speech' },
  { id: 'blood_streaked_mucus', label: 'Blood-streaked mucus' },
  { id: 'swallowing_difficulty', label: 'Trouble swallowing' },
  { id: 'persistent_fever', label: 'Fever that will not settle' },
];
